import { API_BASE } from './api-base';
import { registerGa4Identity } from './firebase-analytics';
import { apiFetch } from './api-client';
import { logSignupIfNewAccount, setAppsFlyerCustomerUserId } from './appsflyer';
import { setFullStoryAttribute } from './fullstory';
import { useEffect, useState } from 'react';


export type Me = {
  /** Stable Wix user id — set as AppsFlyer's customer user id so analytics dedupe per person. */
  userId: string;
  /** The Symphony home metasite, which partitions account-owned data and preferences. */
  metaSiteId?: string;
  /** ISO creation time of the caller's Wix account (`getMyAccount().dateCreated`). Empty when
   *  unavailable. A fresh value drives the web signup event — see `logSignupIfNewAccount`. */
  accountCreatedAt?: string;
  firstName: string;
  lastName: string;
  /** The ISA recipient's display name — the greeting name source, mirroring native. May be empty. */
  recipientName: string;
  /** ISO REGISTRATION time of the caller's ISA recipient — `getMyRecipient().registeredAt` with a
   *  `createdDate` fallback, resolved server-side in `/api/me` (see `resolveRegisteredAt`). Empty when
   *  unavailable (pre-onboarding, or the read failed). Anchors the personal-sale 24h window
   *  (`lib/use-personal-sale.ts`). NOT the Wix ACCOUNT `accountCreatedAt` above — a distinct date. */
  recipientRegisteredAt?: string;
  email: string;
  userName: string;
  /** Wix profile photo URL; empty when the user has none — render initials instead. */
  profileImage?: string;
};

// In-memory singleton — see lib/conversations.ts for the same pattern.
type Store = { value: Me | null; listeners: Set<() => void>; inflight: Promise<void> | null };
const store: Store = { value: null, listeners: new Set(), inflight: null };

/**
 * Fetch `/api/me` once and cache it, applying the AppsFlyer CUID as a side effect. Idempotent:
 * returns the cached value if already fetched, and dedupes concurrent callers on the inflight
 * promise. Resolves the `Me` (or `null` if the fetch fails) so a caller can `await` identity
 * before it needs it — e.g. the signup flow, which must push the CUID before firing the
 * registration event so `af_complete_registration` carries it rather than being stitched later.
 */
export function ensureMe(): Promise<Me | null> {
  if (store.value !== null) return Promise.resolve(store.value);
  if (!store.inflight) {
    store.inflight = (async () => {
      try {
        const res = await apiFetch(`${API_BASE}/api/me`, { credentials: 'include' });
        const data = res.ok ? ((await res.json()) as Me) : null;
        if (data) {
          store.value = data;
          // Identity is known — tie AppsFlyer analytics to the person, not the browser. Idempotent.
          setAppsFlyerCustomerUserId(data.userId);
          // And hand the backend this browser's GA4 client id, so the events our SERVERS produce for
          // this account — a renewal, a credit grant — can be reported into the same GA4 user rather
          // than a phantom one. Same moment for the same reason: it is the first point a person, not a
          // browser, is known. Idempotent; never blocks. Mirrors the phones' connect-tail registration.
          registerGa4Identity(data.userId);
          // wix-recorder identifies by the Wix user id it reads itself; this names it on the recording too, so a
          // session is findable by the id the phones identify with. A no-op unless this session is recorded.
          setFullStoryAttribute('symphonyUserId', data.userId);
          // A brand-new account seen for the first time is a signup — fire once (CUID already set
          // above, so the registration event attributes to the person). Idempotent + freshness-gated.
          logSignupIfNewAccount(data.userId, data.accountCreatedAt);
          store.listeners.forEach((l) => l());
        }
      } catch {
        // swallow — avatar absence is non-blocking
      } finally {
        store.inflight = null;
      }
    })();
  }
  return store.inflight.then(() => store.value);
}

export function useMe(): Me | null {
  const [, force] = useState({});
  useEffect(() => {
    const listener = () => force({});
    store.listeners.add(listener);
    void ensureMe();
    return () => {
      store.listeners.delete(listener);
    };
  }, []);
  return store.value;
}

export function initialsFor(me: Me | null): string {
  if (!me) return '';
  const f = me.firstName?.trim() ?? '';
  const l = me.lastName?.trim() ?? '';
  if (f && l) return (f[0]! + l[0]!).toUpperCase();
  if (f) return f[0]!.toUpperCase();
  if (l) return l[0]!.toUpperCase();
  const fallback = (me.userName || me.email || '').trim();
  return fallback ? fallback[0]!.toUpperCase() : '';
}

/**
 * The single word the app calls you by — the Overview home's title, "Your activity, {name}".
 *
 * It is the SAME rule `GreetingDate` derives inline for the home greeting, and the duplication is
 * deliberate: the Overview home is behind `specs.symphony.SymphonyActivityMapHomeWeb`, `GreetingDate` is not, and a shared
 * helper would have put an experiment's refactor on the live home every account sees. So the greeting
 * keeps its own copy and this one serves the gated screen. `test/first-name-derivation.test.ts` pins
 * that the two expressions stay identical, which is the property the extraction was for.
 *
 * Precedence is the greeting's: the recipient record's name, then the account's first name, then the
 * email's local part — narrowed to the FIRST word, because a full name in "Your activity, …" reads
 * like a form letter.
 *
 * Returns `''` when there is nothing to use (still loading, or an account with no name at all), and
 * every caller must render a no-name variant rather than a dangling separator.
 */
export function firstNameFor(me: Me | null): string {
  if (!me) return '';
  const display = me.recipientName?.trim() || me.firstName?.trim() || me.email?.trim().split('@')[0] || '';
  return display.split(/\s+/)[0] ?? '';
}
